import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {change, Field} from 'redux-form';
import {connect} from "react-redux";
import {compose} from "recompose";
import axios from 'axios';
import {SelectInput} from 'admin-on-rest';
import {RESOURCES as locationResources} from '../fuseLocationsResourceMap';
import {sortArrayByKey} from '../shared/functions';
import {getPayloadLocationFromProps} from '../shared/scheduledReportFunctions';
import {getGetHeaders} from '../shared/serverFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import LocationsTableFilter from './LocationsTableFilter';
import {US_STATES} from '../shared/usStates';
import {filterStyles} from '../shared/styles-filter';

/**
 * A form containing secondary filters and a Location array builder without Date Range
 */
class SecondaryFilterFieldsWithLocations extends Component {
    constructor(props) {
        super(props);
        const vals = (props && props.formValues) ? props.formValues : null;
        this.state = {
            filterListDBAs: [],
            filterListDBAsRetrieved: false,
            filterListDistricts: [],
            filterListDistrictsRetrieved: false,
            filterListLocations: [],
            filterListLocationsRetrieved: false,
            filterListStates: [],
            filterListTypes: [],
            filterLocations: [],
            listsComplete: false,
            selectedFilterType: (vals && vals.filter_types) ? vals.filter_types : 'all',
            selectedFirstChoice: (vals && vals.filter_first_choice) ? vals.filter_first_choice : null,
            selectedSecondChoice: null
        };
        this.checkFilterPopulation = this.checkFilterPopulation.bind(this);
        this.fetchFilterData = this.fetchFilterData.bind(this);
        this.handleLocationsUpdate = this.handleLocationsUpdate.bind(this);
        this.handleSelectFilterType = this.handleSelectFilterType.bind(this);
        this.handleSelectFirstFilterOption = this.handleSelectFirstFilterOption.bind(this);
        this.handleSelectSecondFilterOption = this.handleSelectSecondFilterOption.bind(this);
    }

    componentDidMount() {
        this.fetchFilterData('dbas', locationResources.DBAs.GET_LIST());
        this.fetchFilterData('districts', locationResources.Districts.GET_LIST());
        this.fetchFilterData('locations', locationResources.LocationsMin.GET_LIST());
    }

    /* API Calls ------------------------------ */

    fetchFilterData(type, resource) {

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data.payload && result.data.payload.length > 0) {
                switch (type) {
                    case 'dbas':
                        this.processDBAData(result.data.payload);
                        break;
                    case 'districts':
                        this.processDistrictData(result.data.payload);
                        break;
                    case 'locations':
                        this.processLocationData(result.data.payload);
                        break;
                    default:
                        break;
                }
            } else {
                switch (type) {
                    case 'dbas':
                        this.processDBAData([]);
                        break;
                    case 'districts':
                        this.processDistrictData([]);
                        break;
                    case 'locations':
                        this.processLocationData([]);
                        break;
                    default:
                        break;
                }
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error: ', error.message);
            }
        });
    }

    /* Data Processing ------------------------------ */

    processDBAData(data) {
        let filterListDBAs = [];
        if (data && data.length) {
            for (let d = 0; d < data.length; d++) {
                filterListDBAs.push({id: data[d].id, name: data[d].name});
            }
            filterListDBAs = sortArrayByKey(filterListDBAs, 'name', 'ASC');
        }
        this.setState({
            filterListDBAs: filterListDBAs,
            filterListDBAsRetrieved: true
        }, () => {
            this.checkFilterPopulation();
        });
    }

    processDistrictData(data) {
        let filterListDistricts = [];
        if (data && data.length) {
            for (let d = 0; d < data.length; d++) {
                filterListDistricts.push({
                    id: data[d].id,
                    name: data[d].name,
                    display_name: data[d].system + ' - ' + data[d].name,
                    system: data[d].system
                });
            }
            filterListDistricts = sortArrayByKey(filterListDistricts, 'name', 'ASC');
        }
        this.setState({
            filterListDistricts: filterListDistricts,
            filterListDistrictsRetrieved: true
        }, () => {
            this.checkFilterPopulation();
        });
    }

    processLocationData(data) {
        let filterListLocations = [];
        if (data && data.length) {
            for (let d = 0; d < data.length; d++) {
                filterListLocations.push({
                    city: data[d].city,
                    dba: data[d].dba_id,
                    id: data[d].id,
                    display_name: data[d].id + ' - ' + data[d].name + ' - ' + data[d].city + ', ' + data[d].state,
                    name: data[d].id + ' - ' + data[d].name + ' - ' + data[d].city + ', ' + data[d].state,
                    state: data[d].state
                });
            }
        }
        this.setState({
            filterListLocations: filterListLocations,
            filterListLocationsRetrieved: true
        }, () => {
            this.checkFilterPopulation();
        });
    }

    checkFilterPopulation() {
        if (
            this.state.filterListDBAsRetrieved &&
            this.state.filterListDistrictsRetrieved &&
            this.state.filterListLocationsRetrieved
        ) {
            let filterListTypes = [
                {id: 'all', name: 'All'}
            ];
            if (this.state.filterListDBAs.length > 0) {
                filterListTypes.push({id: 'dba', name: 'by DBA'});
            }
            if (this.state.filterListDistricts.length > 0) {
                filterListTypes.push({id: 'district', name: 'by District'});
            }
            if (this.state.filterListLocations.length > 0) {
                filterListTypes.push({id: 'location', name: 'by Location ID'});
            }
            // Create options for States
            let states = [];
            for (let s = 0; s < US_STATES.length; s++) {
                states.push({id: US_STATES[s].abbreviation, name: US_STATES[s].name});
            }
            states = sortArrayByKey(states, 'id', 'ASC');
            filterListTypes.push({id: 'state', name: 'by State'});
            filterListTypes.push({id: 'locations', name: 'by Specific Locations'});

            this.setState({
                filterListStates: states,
                filterListTypes: filterListTypes,
                listsComplete: true
            });
        }
    }

    /**
     * Populate basic filter with record
     *
     * @param filterType
     * @param recordObj
     */
    getStoredFilterValue(filterType, recordObj) {
        let item = null;
        let value = null;
        if (filterType && recordObj && filterType.id) {
            let type = filterType.id;
            if (type === 'dba' && recordObj.dbas.length > 0) {
                item = this.state.filterListDBAs.find(r => r.id === recordObj.dbas[0]);
                value = (item) ? item.id : null;
            }
            if (type === 'district' && recordObj.districts.length > 0) {
                item = this.state.filterListDistricts.find(r => r.id === recordObj.districts[0]);
                value = (item) ? item.id : null;
            }
            if (type === 'location' && recordObj.locations.length > 0) {
                item = this.state.filterListLocations.find(r => r.id === parseInt(recordObj.locations[0], 10));
                value = (item) ? item.id : null;
            }
            if (type === 'state' && recordObj.states.length > 0) {
                item = this.state.filterListStates.find(r => r.id === recordObj.states[0]);
                value = (item) ? item.id : null;
            }
        }
        return (value) ? value : null;
    }

    /**
     * Handle selection event for Filter Type
     *
     * @param event
     * @param index
     * @param value
     */
    handleSelectFilterType(event, index, value) {
        if (index) {
            let filterObj = {};
            let filterType = this.state.filterListTypes.find(f => f.id === index);
            if (filterType) {
                filterObj = filterType;
            }
            // If Edit form, populate filter value
            let filterValue = null;
            if (this.props.type === 'edit') {
                const recordObj = getPayloadLocationFromProps(this.props);
                filterValue = this.getStoredFilterValue(filterType, recordObj);
            }

            this.setState({
                selectedFilterType: index,
                selectedFirstChoice: filterValue,
                selectedSecondChoice: null
            }, () => {
                this.updateFilterChoice(filterObj, null, null);
                if (this.props.type === 'edit') {
                    this.updateFilterChoice(null, filterValue, null);
                }
                // If Secondary filter is set to All, clear any second selection
                if (filterObj === 'all') {
                    this.updateFilterChoice(null, null, 'none');
                }
            });
        }
    }

    /**
     * Handle selection event for First Filter Choice
     *
     * @param event
     * @param index
     * @param value
     */
    handleSelectFirstFilterOption(event, index, value) {
        if (index) {
            this.setState({
                selectedFirstChoice: index
            }, () => {
                this.updateFilterChoice(null, index, 'none');
            });
        }
    }

    /**
     * Lift Filter Choices to parent component
     *
     * @param type
     * @param first
     * @param second
     */
    updateFilterChoice = (type, first, second) => {
        if (type) {
            if (typeof this.props.onFilterTypeSelect === 'function') {
                this.props.onFilterTypeSelect(type);
            }
        } else {
            if (typeof this.props.onFilterChoiceSelect === 'function') {
                this.props.onFilterChoiceSelect(first, second);
            }
        }
    };

    /**
     * Handle selection event for Second Filter Choice
     *
     * @param event
     * @param index
     * @param value
     */
    handleSelectSecondFilterOption(event, index, value) {
        if (index) {
            this.setState({
                selectedSecondChoice: index
            }, () => {
                // Also pass the current first filter choice
                if (typeof this.props.onFilterChoiceSelect === 'function') {
                    this.props.onFilterChoiceSelect(this.state.selectedFirstChoice, index);
                }
            });
        }
    }

    handleLocationsUpdate(list) {
        const locations = [];
        for (let i = 0; i < list.length; i++) {
            locations.push(list[i].id.toString());
        }
        this.setState({
            filterLocations: locations
        }, () => {
            if (typeof this.props.onFilterLocations === 'function') {
                this.props.onFilterLocations(this.state.filterLocations);
            }
            if (typeof this.props.formName === 'string') {
                this.props.dispatch(change(this.props.formName, 'payload.location.locations', this.state.filterLocations));
            }
        })
    }

    render() {
        const {
            displayFields,
            includeLocationSubmit,
            record
        } = this.props;

        const {
            filterListDBAs,
            filterListDistricts,
            filterListLocations,
            filterListStates,
            filterListTypes,
            listsComplete,
            selectedFilterType,
            selectedFirstChoice
        } = this.state;

        // Create labels and set option lists based on Filter Type
        let firstFilterLabel = '';
        let firstFilterOptions = [];

        if (selectedFilterType === 'dba') {
            firstFilterLabel = 'Select a DBA';
            firstFilterOptions = filterListDBAs;
        } else if (selectedFilterType === 'district') {
            firstFilterLabel = 'Select a District';
            firstFilterOptions = filterListDistricts;
        } else if (selectedFilterType === 'location') {
            firstFilterLabel = 'Select a Location';
            firstFilterOptions = filterListLocations;
        } else if (selectedFilterType === 'state') {
            firstFilterLabel = 'Select a State';
            firstFilterOptions = filterListStates;
        }

        // Create styles for first select filter (for States)
        const firstStyle = (selectedFilterType === 'state')
            ? filterStyles.selectInputSmall
            : filterStyles.selectInputWide;

        if (!displayFields) {
            return (
                <div className="list-filter">
                    <hr style={filterStyles.break}/>
                </div>
            )
        }

        // @TODO populate filters properly

        return (
            <div className="list-filter">
                {listsComplete && filterListTypes && filterListTypes.length > 0 &&
                    <Field name="filter_types"
                           component={SelectInput}
                           label="Filter Summaries:"
                           choices={filterListTypes}
                           onChange={this.handleSelectFilterType}
                           elStyle={filterStyles.selectInput}
                           options={{
                               value: selectedFilterType
                           }}
                    />
                }
                {listsComplete && selectedFilterType && selectedFilterType !== 'all' &&
                    <span>
                        {firstFilterOptions.length > 0 &&
                            <Field name="filter_first_choice"
                                   component={SelectInput}
                                   label={firstFilterLabel}
                                   choices={firstFilterOptions}
                                   onChange={this.handleSelectFirstFilterOption}
                                   elStyle={firstStyle}
                                   options={{
                                       value: selectedFirstChoice
                                   }}
                            />
                        }
                        {selectedFilterType === 'locations' &&
                            <LocationsTableFilter
                                record={record}
                                includeSubmit={includeLocationSubmit}
                                onLocationUpdate={this.handleLocationsUpdate}
                                onLocations={this.handleSelectSecondFilterOption}
                            />
                        }
                    </span>
                }
                <hr style={filterStyles.break}/>
            </div>
        );
    }
}

const mapStateToProps = (state, props) => {
    const formName = props.formName;
    if (formName) {
        let form = state.form[formName];
        if (form && form.values) {
            return {
                formValues: form.values
            }
        }
    }
    return {}
};

SecondaryFilterFieldsWithLocations.propTypes = {
    displayFields: PropTypes.bool,
    formName: PropTypes.string,
    includeLocationSubmit: PropTypes.bool,
    onFilterTypeSelect: PropTypes.func,
    onFilterChoiceSelect: PropTypes.func,
    onFilterLocations: PropTypes.func,
    type: PropTypes.string
};

SecondaryFilterFieldsWithLocations.defaultProps = {
    displayFields: false,
    includeLocationSubmit: true,
    type: 'create'
};

const enhance = compose(
    connect(mapStateToProps)
);

export default enhance(SecondaryFilterFieldsWithLocations);



// WEBPACK FOOTER //
// ./src/Fuse/components/SecondaryFilterFieldsWithLocations.js